import { clamp, raycast, type EnemyKind, type Simulation, type WeaponId } from './core';
import { ENVIRONMENT_TEXTURES, themeFor } from './environment';

export type RenderOptions = { effects: boolean; minimap: boolean; low: boolean };
const COLORS: Record<EnemyKind, string> = { grunt: '#728a57', runner: '#b88843', spitter: '#678ec0', brute: '#a15d58', boss: '#b63448' };
const ENEMY_LABELS: Record<EnemyKind, string> = { grunt: 'SOLDAT', runner: 'ÉCLAIREUR', spitter: 'TIREUR', brute: 'BRUTE', boss: 'BOSS' };
function pixelSprite(kind: EnemyKind, frame: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas'); canvas.width = 32; canvas.height = 48;
  const c = canvas.getContext('2d')!;
  const block = (color: string, x: number, y: number, w: number, h: number) => { c.fillStyle = color; c.fillRect(x, y, w, h); };
  const heavy = kind === 'boss' || kind === 'brute';
  block('#171d1b', 7, 37, 8, 11 - frame * 2); block('#171d1b', 18, 37, 8, 9 + frame * 2);
  block(COLORS[kind], 6, 15, 22, 24); block('#333e32', 11, 28, 11, 11);
  block('#202b23', 3, 18, 5, 19); block(COLORS[kind], 25, 18, 6, 15);
  block('#1c251e', 9, 3, 16, 15); block(COLORS[kind], 8, 2, 18, 6);
  block('#ba8e72', 11, 9, 12, 8); block('#2a2321', 10, 10, 14, 4);
  block(kind === 'spitter' ? '#8cffff' : '#ff653d', 11, 10, 4, 2); block('#ffc55f', 19, 10, 4, 2);
  block('#111817', 10, 20, 15, 4); block('#99aa82', 11, 18, 12, 2);
  block('#c3b173', 8, 32, 19, 3); block('#28332d', 19, 24, 5, 12);
  if (heavy) { block(COLORS[kind], 1, 14, 9, 10); block(COLORS[kind], 24, 14, 8, 10); block('#e3c592', 4, 0, 4, 10); block('#e3c592', 26, 0, 4, 10); }
  if (kind === 'spitter') { block('#6bced8', 1, 29, 8, 8); block('#d6ffff', 3, 31, 4, 4); }
  return canvas;
}

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private textures = new Map<string, HTMLImageElement>();
  private materials = new Map<string, HTMLCanvasElement>();
  private surfacePixels = new Map<string, Uint8ClampedArray>();
  private lighting: { level: Simulation['level']; width: number; height: number; data: Float32Array } | null = null;
  private enemyTexture: HTMLImageElement | null = null;
  private weaponTextures: Partial<Record<WeaponId, HTMLImageElement>> = {};
  private muzzleTexture: HTMLImageElement | null = null;
  private sprites = new Map<string, HTMLCanvasElement>();
  private depth = new Float32Array(640);
  private backdrop = document.createElement('canvas');
  private floorImage: ImageData;
  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D indisponible');
    this.ctx = ctx;
    canvas.width = 640; canvas.height = 360;
    this.backdrop.width = 320; this.backdrop.height = 180;
    this.floorImage = new ImageData(320, 180);
    for (const kind of Object.keys(COLORS) as EnemyKind[]) for (let frame = 0; frame < 2; frame++) this.sprites.set(`${kind}${frame}`, pixelSprite(kind, frame));
  }
  async load(): Promise<number> {
    let failures = 0;
    const walls = ENVIRONMENT_TEXTURES.map(async name => {
      const img = new Image(); img.src = `assets/textures/environment/${name}.jpg`;
      try {
        await img.decode(); this.textures.set(name, img);
        const canvas = document.createElement('canvas'); canvas.width = canvas.height = 128;
        const context = canvas.getContext('2d')!; context.drawImage(img, 0, 0, 128, 128);
        this.surfacePixels.set(name, context.getImageData(0, 0, 128, 128).data);
      } catch { failures++; }
    });
    // Original artwork and alpha channels; procedural graphics only serve as a fallback.
    const artwork: Array<[string, (image: HTMLImageElement) => void]> = [
      ['demon.png', image => { this.enemyTexture = image; }],
      ['demon_gun.png', image => { this.weaponTextures.pistol = image; }],
      ['demon_shotgun.png', image => { this.weaponTextures.shotgun = image; }],
      ['demon_gatling.png', image => { this.weaponTextures.gatling = image; }],
      ['flame_shot.png', image => { this.muzzleTexture = image; }],
    ];
    await Promise.all([...walls, ...artwork.map(async ([name, assign]) => {
      const image = new Image(); image.src = `assets/textures/${name}`;
      try { await image.decode(); assign(image); } catch { failures++; }
    })]);
    this.materials.clear();
    return failures;
  }
  render(sim: Simulation, options: RenderOptions, playing: boolean) {
    const c = this.ctx, w = this.canvas.width, h = this.canvas.height;
    const p = sim.player, dx = Math.cos(p.angle), dy = Math.sin(p.angle), planeX = -dy * .66, planeY = dx * .66;
    const theme = themeFor(sim.level);
    c.imageSmoothingEnabled = false;
    this.surfaces(sim, dx, dy, planeX, planeY, options);
    const stride = options.low ? 2 : 1;
    for (let x = 0; x < w; x += stride) {
      const camera = 2 * x / w - 1, rx = dx + planeX * camera, ry = dy + planeY * camera;
      const hit = raycast(sim.level, p, rx, ry);
      for (let n = 0; n < stride; n++) this.depth[x + n] = hit.distance;
      const height = h / Math.max(.02, hit.distance), top = (h - height) / 2;
      const start = Math.max(0, top), end = Math.min(h, top + height);
      const material = sim.level.wallMaterials?.[hit.y * sim.level.width + hit.x] ?? (hit.tile === 2 ? 1 : 0);
      const texture = this.wallMaterial(sim, hit.tile === 3 ? 3 : material);
      if (texture) {
        let u = Math.floor(hit.u * texture.width);
        if (hit.side === 0 && rx > 0 || hit.side === 1 && ry < 0) u = texture.width - u - 1;
        c.drawImage(texture, u, (start - top) / height * texture.height, 1, (end - start) / height * texture.height, x, start, stride, end - start);
      } else { c.fillStyle = hit.side ? '#506452' : '#779279'; c.fillRect(x, start, stride, end - start); }
      const lx = p.x + rx * Math.max(0, hit.distance - .15), ly = p.y + ry * Math.max(0, hit.distance - .15);
      const lightIndex = (clamp(Math.floor(ly * 4), 0, this.lighting!.height - 1) * this.lighting!.width + clamp(Math.floor(lx * 4), 0, this.lighting!.width - 1)) * 3;
      const brightness = (this.lighting!.data[lightIndex] + this.lighting!.data[lightIndex + 1] + this.lighting!.data[lightIndex + 2]) / 3;
      c.fillStyle = `rgba(${theme.fog.join(',')},${clamp(1 - brightness + hit.distance / 26 + hit.side * .12, .06, .9)})`;
      c.fillRect(x, start, stride, end - start);
    }
    type Billboard = { x: number; y: number; size: number; lift: number; sprite?: HTMLCanvasElement | HTMLImageElement; color: string; aspect?: number; alpha?: number; luminous?: boolean; enemy?: typeof sim.enemies[number] };
    const objects: Billboard[] = sim.enemies.filter(e => e.hp > 0).map(e => ({ x: e.x, y: e.y, size: e.kind === 'boss' ? 1.28 : e.kind === 'brute' ? 1.1 : .95, lift: options.effects && !(e.telegraph ?? 0) ? Math.abs(Math.sin(sim.time * (e.kind === 'runner' ? 12 : 7) + e.id)) * .012 : 0, sprite: this.enemyTexture ?? this.sprites.get(`${e.kind}${options.effects ? Math.floor(sim.time * 5 + e.id) % 2 : 0}`), color: COLORS[e.kind], enemy: e }));
    sim.pickups.forEach(item => objects.push({ ...item, size: .3, lift: options.effects ? Math.sin(sim.time * 3) * .025 : 0, sprite: this.pickupSprite(item.kind), color: '#c1ff91' }));
    sim.bullets.forEach(b => objects.push({ ...b, size: .18, lift: .42, sprite: this.effectSprite('projectile'), color: '#9cfcff', luminous: true }));
    sim.level.lights?.forEach(light => objects.push({ ...light, size: .055, lift: .94, sprite: this.lightSprite(light.color), color: light.color, luminous: true }));
    for (const corpse of sim.corpses) {
      const fall = options.effects ? clamp((corpse.maxLife - corpse.life) / .32, 0, 1) : 1;
      const originalSize = corpse.kind === 'boss' ? 1.28 : corpse.kind === 'brute' ? 1.1 : .95;
      const height = originalSize * (1 - fall * .86);
      objects.push({ ...corpse, size: height, lift: 0, sprite: this.enemyTexture ?? this.sprites.get(`${corpse.kind}0`), color: COLORS[corpse.kind], aspect: originalSize * .65 / height, alpha: clamp(corpse.life / .6, 0, .75) });
    }
    if (options.effects) for (const impact of sim.impacts) {
      const life = impact.life / impact.maxLife;
      objects.push({ ...impact, size: .13 + (1 - life) * .2, lift: .42, sprite: this.effectSprite(impact.kind), color: '#ffe2ac', alpha: life, luminous: true });
    }
    for (const enemy of sim.enemies) if ((enemy.telegraph ?? 0) > 0 && enemy.attackKind === 'charge') {
      const ax = Math.cos(enemy.aimAngle ?? 0), ay = Math.sin(enemy.aimAngle ?? 0);
      const length = Math.min(4.6, raycast(sim.level, enemy, ax, ay).distance - .1);
      for (let offset = .6; offset < length; offset += .45) objects.push({ x: enemy.x + ax * offset, y: enemy.y + ay * offset, size: .028, aspect: 8, lift: .012, color: '#ff956a', luminous: true });
    }
    objects.sort((a, b) => ((b.x - p.x) * dx + (b.y - p.y) * dy) - ((a.x - p.x) * dx + (a.y - p.y) * dy));
    for (const obj of objects) {
      const sx = obj.x - p.x, sy = obj.y - p.y;
      const depth = sx * dx + sy * dy, lateral = (-sx * dy + sy * dx) / .66;
      if (depth < .08) continue;
      const height = h * obj.size / depth, width = height * (obj.aspect ?? (obj.sprite ? obj.sprite.width / obj.sprite.height : 1));
      const center = w / 2 * (1 + lateral / depth), left = center - width / 2;
      const top = h / 2 + h * (.5 - obj.lift) / depth - height;
      const startY = Math.max(0, top), endY = Math.min(h, top + height);
      if (endY <= startY) continue;
      for (let x = Math.max(0, Math.floor(left)); x < Math.min(w, left + width); x++) {
        if (depth >= this.depth[x]) continue;
        if (obj.sprite) {
          const u = clamp(Math.floor((x - left) / width * obj.sprite.width), 0, obj.sprite.width - 1);
          c.globalAlpha = (obj.alpha ?? 1) * (obj.luminous ? 1 : clamp(1 - depth / 36, .4, 1));
          c.drawImage(obj.sprite, u, (startY - top) / height * obj.sprite.height, 1, (endY - startY) / height * obj.sprite.height, x, startY, 1, endY - startY);
          c.globalAlpha = 1;
        } else { c.fillStyle = obj.color; c.fillRect(x, startY, 1, endY - startY); }
      }
      if (obj.enemy && depth < 10 && center >= 0 && center < w && depth < this.depth[Math.floor(center)]) {
        const enemy = obj.enemy, warning = (enemy.telegraph ?? 0) > 0, exposed = (enemy.vulnerable ?? 0) > 0;
        const bar = Math.min(64, width), y = Math.max(20, top - 8);
        c.fillStyle = '#111'; c.fillRect(center - bar / 2, y, bar, 3);
        c.fillStyle = enemy.flash > 0 ? '#fff' : exposed ? '#caff97' : warning ? '#ffc781' : COLORS[enemy.kind]; c.fillRect(center - bar / 2, y, bar * Math.max(0, enemy.hp / enemy.maxHp), 3);
        c.font = '9px monospace'; c.textAlign = 'center'; c.lineWidth = 3; c.strokeStyle = '#07100c';
        const labelY = Math.max(12, y - 5), label = warning ? enemy.attackKind === 'charge' ? '! CHARGE' : enemy.attackKind === 'shot' ? '! TIR' : '! FRAPPE' : exposed ? 'EXPOSÉ ×1,6' : ENEMY_LABELS[enemy.kind];
        c.strokeText(label, center, labelY); c.fillText(label, center, labelY); c.textAlign = 'start';
        if (warning) {
          c.fillStyle = '#321d16'; c.fillRect(center - bar / 2, y + 5, bar, 3);
          c.fillStyle = '#ffc781'; c.fillRect(center - bar / 2, y + 5, bar * (1 - enemy.telegraph! / enemy.telegraphMax!), 3);
        }
      }
    }
    if (playing) {
      this.weapon(sim, options.effects);
      c.strokeStyle = sim.killMarker > 0 ? '#ff8a68' : sim.hitMarker > 0 ? '#c8ff81' : '#e4eee2'; c.lineWidth = 1;
      const gap = 4 + sim.recoil * 5;
      c.beginPath(); c.moveTo(w / 2 - gap - 5, h / 2); c.lineTo(w / 2 - gap, h / 2); c.moveTo(w / 2 + gap, h / 2); c.lineTo(w / 2 + gap + 5, h / 2); c.moveTo(w / 2, h / 2 - gap - 5); c.lineTo(w / 2, h / 2 - gap); c.moveTo(w / 2, h / 2 + gap); c.lineTo(w / 2, h / 2 + gap + 5); c.stroke();
      if (sim.hitMarker > 0 || sim.killMarker > 0) { c.beginPath(); for (const [x, y] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) { c.moveTo(w / 2 + x * 5, h / 2 + y * 5); c.lineTo(w / 2 + x * 9, h / 2 + y * 9); } c.stroke(); }
      if (sim.hurt > 0 && options.effects) { c.strokeStyle = `rgba(230,65,40,${sim.hurt * 2})`; c.lineWidth = 16; c.strokeRect(0, 0, w, h); }
      if (options.minimap) this.minimap(sim);
      const boss = sim.enemies.find(enemy => enemy.kind === 'boss' && enemy.hp > 0);
      if (boss) {
        const x = w - 195, y = 15, exposed = (boss.vulnerable ?? 0) > 0;
        c.fillStyle = '#081015d9'; c.fillRect(x - 7, y - 6, 188, 33);
        c.fillStyle = exposed ? '#caff97' : '#ee9983'; c.font = '9px monospace';
        c.fillText(exposed ? 'UNITÉ LOURDE / EXPOSÉE' : (boss.charging ?? 0) > 0 ? 'UNITÉ LOURDE / CHARGE' : 'UNITÉ LOURDE', x, y + 5);
        c.fillStyle = '#403032'; c.fillRect(x, y + 13, 174, 4);
        c.fillStyle = exposed ? '#caff97' : '#d3735d'; c.fillRect(x, y + 13, 174 * clamp(boss.hp / boss.maxHp, 0, 1), 4);
      }
    }
  }
  private surfaces(sim: Simulation, dx: number, dy: number, planeX: number, planeY: number, options: RenderOptions) {
    const theme = themeFor(sim.level), level = sim.level;
    if (this.lighting?.level !== level) {
      const width = level.width * 4, height = level.height * 4, data = new Float32Array(width * height * 3);
      for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 3;
        for (let channel = 0; channel < 3; channel++) data[index + channel] = .7 * theme.light[channel];
        for (const light of level.lights ?? []) {
          const distance = Math.hypot(x / 4 + .125 - light.x, y / 4 + .125 - light.y);
          if (distance >= light.radius) continue;
          const power = .62 * (1 - distance / light.radius) ** 2;
          for (let channel = 0; channel < 3; channel++) data[index + channel] += parseInt(light.color.slice(1 + channel * 2, 3 + channel * 2), 16) / 255 * power;
        }
      }
      this.lighting = { level, width, height, data };
    }
    const light = this.lighting, pixels = this.floorImage.data;
    const floor = this.surfacePixels.get(theme.floor), ceiling = this.surfacePixels.get(theme.ceiling);
    // Both planes share the same projected world coordinates and one reusable buffer.
    const step = options.low ? 2 : 1;
    for (let y = 90; y < 180; y++) {
      const distance = 90 / (y - 89.5), fog = clamp(distance / 26, 0, .96);
      let fx = sim.player.x + distance * (dx - planeX), fy = sim.player.y + distance * (dy - planeY);
      const stepX = distance * 2 * planeX / 320, stepY = distance * 2 * planeY / 320;
      for (let x = 0; x < 320; x += step) {
        const tex = (((Math.floor(fy * 128) & 127) * 128) + (Math.floor(fx * 128) & 127)) * 4;
        const li = (clamp(Math.floor(fy * 4), 0, light.height - 1) * light.width + clamp(Math.floor(fx * 4), 0, light.width - 1)) * 3;
        const bottom = (y * 320 + x) * 4, top = ((179 - y) * 320 + x) * 4;
        for (let channel = 0; channel < 3; channel++) {
          const power = light.data[li + channel] + (options.effects ? sim.recoil * .15 / (1 + distance) : 0);
          pixels[bottom + channel] = (floor?.[tex + channel] ?? 70) * power * .88 * (1 - fog) + theme.fog[channel] * fog;
          pixels[top + channel] = (ceiling?.[tex + channel] ?? 45) * power * .44 * (1 - fog) + theme.fog[channel] * fog;
          if (step === 2) { pixels[bottom + channel + 4] = pixels[bottom + channel]; pixels[top + channel + 4] = pixels[top + channel]; }
        }
        pixels[bottom + 3] = pixels[top + 3] = 255;
        if (step === 2) pixels[bottom + 7] = pixels[top + 7] = 255;
        fx += stepX * step; fy += stepY * step;
      }
    }
    this.backdrop.getContext('2d')!.putImageData(this.floorImage, 0, 0);
    this.ctx.drawImage(this.backdrop, 0, 0, this.canvas.width, this.canvas.height);
  }
  private wallMaterial(sim: Simulation, material: number): HTMLCanvasElement | undefined {
    const theme = themeFor(sim.level), key = `${theme.name}-${material}`;
    if (this.materials.has(key)) return this.materials.get(key);
    const image = this.textures.get(material === 3 ? 'metal_plate' : theme.walls[material]);
    if (!image) return undefined;
    const canvas = document.createElement('canvas'); canvas.width = canvas.height = 512;
    const c = canvas.getContext('2d')!; c.drawImage(image, 0, 0, 512, 512);
    const crypt = sim.level.theme === 'crypt' || sim.level.id === 'maze';
    c.fillStyle = '#070c105c'; c.fillRect(0, 0, 512, 26); c.fillRect(0, 468, 512, 44);
    c.fillStyle = '#bfc9cd30'; c.fillRect(0, 27, 512, 3); c.fillRect(0, 466, 512, 3);
    if (!crypt || material === 3) {
      c.fillStyle = '#11181b'; c.fillRect(0, 0, 9, 512); c.fillRect(503, 0, 9, 512);
      c.fillStyle = '#a8b1b666'; c.fillRect(9, 0, 3, 512); c.fillRect(500, 0, 3, 512);
    }
    if (material === 1 || material === 3) {
      c.fillStyle = '#11191c'; c.fillRect(146, 54, 220, 35);
      c.fillStyle = theme.accent; c.fillRect(162, 64, 188, 9);
      c.globalAlpha = .12; c.fillRect(146, 48, 220, 50); c.globalAlpha = 1;
    }
    if (material === 1 && sim.level.theme === 'foundry') {
      c.fillStyle = '#0a1016'; c.fillRect(49, 144, 414, 255);
      const heat = c.createLinearGradient(0, 158, 0, 390); heat.addColorStop(0, '#441c16'); heat.addColorStop(.6, '#dc6e2b'); heat.addColorStop(1, '#ffd38a');
      c.fillStyle = heat; c.fillRect(66, 162, 380, 219);
      c.fillStyle = '#182025'; for (let x = 76; x < 446; x += 33) c.fillRect(x, 158, 13, 230);
      c.fillRect(61, 231, 390, 13); c.fillRect(61, 316, 390, 13);
      c.fillStyle = '#a1815b'; c.fillRect(50, 143, 412, 5); c.fillRect(50, 397, 412, 5);
    }
    if (material === 1 && sim.level.theme === 'reactor') {
      c.fillStyle = '#14252d'; c.fillRect(48, 129, 416, 274);
      for (let x = 74; x <= 378; x += 76) {
        c.fillStyle = '#080e15'; c.fillRect(x, 149, 52, 230);
        c.fillStyle = '#27696c'; c.fillRect(x + 8, 161, 36, 205);
        c.fillStyle = '#83e4bf'; c.fillRect(x + 17, 167, 9, 194);
        c.fillStyle = '#20303b'; c.fillRect(x + 4, 226, 44, 11); c.fillRect(x + 4, 298, 44, 11);
      }
    }
    if (material < 2 && sim.level.theme === 'hangar') {
      for (let x = 40; x < 480; x += 56) { c.fillStyle = '#060e193d'; c.fillRect(x, 110, 14, 316); c.fillStyle = '#d5e6ee24'; c.fillRect(x + 14, 110, 3, 316); }
      c.fillStyle = '#111d24'; c.fillRect(147, 211, 218, 69); c.fillStyle = '#d3c098'; c.font = 'bold 30px monospace'; c.textAlign = 'center'; c.fillText('CARGO', 256, 256);
    }
    if (material === 2 && !crypt || material === 3) {
      c.fillStyle = '#d5ac60'; c.fillRect(12, 426, 488, 28);
      c.fillStyle = '#172023';
      for (let x = 12; x < 488; x += 48) { c.beginPath(); c.moveTo(x, 454); c.lineTo(x + 28, 426); c.lineTo(x + 47, 426); c.lineTo(x + 19, 454); c.fill(); }
    }
    if (material === 3) {
      c.fillStyle = '#070e13'; c.fillRect(250, 92, 12, 374);
      c.strokeStyle = '#a8b7b0'; c.lineWidth = 3; c.strokeRect(38, 117, 189, 268); c.strokeRect(285, 117, 189, 268);
      c.fillStyle = '#a3e3d0'; c.fillRect(218, 251, 16, 30); c.fillRect(278, 251, 16, 30);
      c.fillStyle = '#151e23'; c.fillRect(179, 147, 154, 55); c.fillStyle = '#d3dfd6';
      c.font = 'bold 22px monospace'; c.textAlign = 'center'; c.fillText('ACCESS', 256, 183);
    }
    this.materials.set(key, canvas); return canvas;
  }
  private lightSprite(color: string) {
    const key = `light-${color}`;
    if (!this.sprites.has(key)) {
      const canvas = document.createElement('canvas'); canvas.width = 64; canvas.height = 16;
      const c = canvas.getContext('2d')!;
      c.fillStyle = '#10191e'; c.fillRect(0, 2, 64, 12);
      c.fillStyle = color; c.globalAlpha = .25; c.fillRect(2, 0, 60, 16); c.globalAlpha = 1; c.fillRect(4, 6, 56, 4);
      c.fillStyle = '#fff9e3'; c.fillRect(8, 7, 48, 2);
      this.sprites.set(key, canvas);
    }
    return this.sprites.get(key)!;
  }
  private effectSprite(kind: string) {
    const key = `effect-${kind}`;
    if (!this.sprites.has(key)) {
      const canvas = document.createElement('canvas'); canvas.width = canvas.height = 32;
      const c = canvas.getContext('2d')!;
      const color = kind === 'projectile' ? '#8eedf4' : kind === 'hit' ? '#fb9a6b' : '#fbd69b';
      c.fillStyle = color; c.globalAlpha = .15; c.beginPath(); c.arc(16, 16, 15, 0, Math.PI * 2); c.fill(); c.globalAlpha = 1;
      if (kind === 'projectile') { c.beginPath(); c.arc(16, 16, 7, 0, Math.PI * 2); c.fill(); }
      else { c.beginPath(); for (let i = 0; i < 16; i++) { const angle = i * Math.PI / 8, radius = i % 2 ? 4 : i % 4 ? 10 : 15; c.lineTo(16 + Math.cos(angle) * radius, 16 + Math.sin(angle) * radius); } c.closePath(); c.fill(); }
      c.fillStyle = '#fff7d9'; c.fillRect(13, 13, 6, 6); this.sprites.set(key, canvas);
    }
    return this.sprites.get(key)!;
  }
  private pickupSprite(kind: string) {
    const key = `pickup-${kind}`;
    if (!this.sprites.has(key)) {
      const s = document.createElement('canvas'); s.width = s.height = 16;
      const c = s.getContext('2d')!; c.fillStyle = '#16271e'; c.fillRect(1, 1, 14, 14);
      c.strokeStyle = kind === 'health' ? '#8af595' : kind === 'ammo' ? '#edc471' : '#76c3e7'; c.strokeRect(1, 1, 14, 14); c.fillStyle = c.strokeStyle;
      if (kind === 'health') { c.fillRect(6, 3, 4, 10); c.fillRect(3, 6, 10, 4); }
      else if (kind === 'ammo') { for (let x = 4; x <= 10; x += 3) c.fillRect(x, 4, 2, 8); }
      else { c.beginPath(); c.moveTo(4, 3); c.lineTo(12, 3); c.lineTo(12, 9); c.lineTo(8, 13); c.lineTo(4, 9); c.fill(); }
      this.sprites.set(key, s);
    }
    return this.sprites.get(key)!;
  }
  private weapon(sim: Simulation, effects: boolean) {
    const c = this.ctx, recoil = effects ? sim.recoil : 0;
    const imported = this.weaponTextures[sim.weapon];
    if (imported) {
      const height = this.canvas.height * (sim.weapon === 'shotgun' ? .53 : sim.weapon === 'gatling' ? .49 : .5);
      const width = height * imported.width / imported.height;
      const swayX = effects ? Math.sin(sim.time * 2) * 2 + (sim.weapon === 'gatling' ? Math.sin(sim.time * 75) * sim.gatlingSpin * .7 : 0) : 0;
      const x = (this.canvas.width - width) / 2 + swayX;
      const y = this.canvas.height - height + 18 + recoil * (sim.weapon === 'shotgun' ? 20 : 12);
      if (effects && sim.recoil > (sim.weapon === 'gatling' ? .25 : .55) && this.muzzleTexture) {
        const flashHeight = height * (sim.weapon === 'shotgun' ? .65 : sim.weapon === 'gatling' ? .36 : .48), flashWidth = flashHeight * this.muzzleTexture.width / this.muzzleTexture.height;
        const muzzleX = x + width * (sim.weapon === 'pistol' ? .69 : sim.weapon === 'gatling' ? .29 : .5);
        c.save(); c.globalAlpha = .85; c.globalCompositeOperation = 'lighter';
        c.drawImage(this.muzzleTexture, muzzleX - flashWidth / 2, y - flashHeight * .65, flashWidth, flashHeight);
        c.restore();
      }
      c.drawImage(imported, x, y, width, height);
      return;
    }
    const x = 320 + (effects ? Math.sin(sim.time * 2) * 2 : 0), y = 360 + recoil * 12;
    c.save(); c.translate(x, y);
    const wide = sim.weapon === 'gatling' ? 53 : sim.weapon === 'shotgun' ? 35 : 23;
    c.fillStyle = '#121b19'; c.beginPath(); c.moveTo(-wide * 2, 0); c.lineTo(-wide, -60); c.lineTo(wide, -60); c.lineTo(wide * 2, 0); c.fill();
    c.fillStyle = '#434c43'; c.fillRect(-wide, -75, wide * 2, 77);
    c.fillStyle = '#737e68'; c.fillRect(-wide + 4, -72, wide - 5, 75);
    c.fillStyle = '#a5b08d'; c.fillRect(-wide + 6, -67, 4, 45);
    const barrels = sim.weapon === 'gatling' ? 3 : sim.weapon === 'shotgun' ? 2 : 1;
    for (let i = 0; i < barrels; i++) { const bx = (i - (barrels - 1) / 2) * 15; c.fillStyle = '#171e1b'; c.fillRect(bx - 6, -100, 12, 53); c.fillStyle = '#667467'; c.fillRect(bx - 5, -100, 3, 45); c.fillStyle = '#080e0b'; c.fillRect(bx - 4, -104, 9, 6); }
    c.fillStyle = '#ce8753'; c.fillRect(-wide - 13, -25, 16, 28); c.fillRect(wide - 3, -25, 16, 28);
    c.fillStyle = '#d4ba76'; c.fillRect(-7, -48, 14, 7);
    if (recoil > .65) { c.fillStyle = '#ffc76b'; c.beginPath(); c.moveTo(-18, -107); c.lineTo(-6, -125); c.lineTo(0, -144); c.lineTo(6, -126); c.lineTo(18, -107); c.fill(); c.fillStyle = '#fff3c7'; c.fillRect(-4, -125, 8, 22); }
    c.restore();
  }
  private minimap(sim: Simulation) {
    const c = this.ctx, size = 100, scale = Math.min(4, size / Math.max(sim.level.width, sim.level.height));
    const w = sim.level.width * scale, h = sim.level.height * scale;
    c.fillStyle = '#07100cdd'; c.fillRect(10, 10, w + 10, h + 10);
    for (let i = 0; i < sim.level.data.length; i++) {
      if (!sim.explored[i]) continue;
      c.fillStyle = sim.level.data[i] === 3 ? '#d3ac61' : sim.level.data[i] ? '#728270' : '#1c3024';
      c.fillRect(15 + i % sim.level.width * scale, 15 + Math.floor(i / sim.level.width) * scale, scale, scale);
    }
    for (const e of sim.enemies) if (Math.hypot(e.x - sim.player.x, e.y - sim.player.y) < 5) { c.fillStyle = '#f2775a'; c.fillRect(14 + e.x * scale, 14 + e.y * scale, 2, 2); }
    for (const item of sim.pickups) if (sim.explored[Math.floor(item.y) * sim.level.width + Math.floor(item.x)]) { c.fillStyle = item.kind === 'health' ? '#8af595' : item.kind === 'ammo' ? '#edc471' : '#76c3e7'; c.fillRect(14 + item.x * scale, 14 + item.y * scale, 2, 2); }
    const x = 15 + sim.player.x * scale, y = 15 + sim.player.y * scale;
    c.fillStyle = '#d1ff91'; c.fillRect(x - 1, y - 1, 3, 3); c.strokeStyle = '#d1ff91'; c.lineWidth = 1;
    c.beginPath(); c.moveTo(x, y); c.lineTo(x + Math.cos(sim.player.angle) * 7, y + Math.sin(sim.player.angle) * 7); c.stroke();
  }
}
